import { abs } from "~/tools/abs";
import type { Time } from "~/tools/date-time/time";
import { Impulse, type Scope, batch } from "~/tools/impulse";
import { max } from "~/tools/max";

import type { TimelineBrushAreaState } from "./TimelineBrushArea";

const IS_BRUSHING_THRESHOLD_PX = 2;

export class TimelineControlsState {
	private readonly brushStart = Impulse<{ time: Time; px?: number }>({ time: -1 });
	private readonly hovering = Impulse(false);
	private readonly brushing = Impulse(false);
	private readonly preventsClick = Impulse(false);

	public readonly x: Impulse<number>;

	private readonly _shift = Impulse(-1);

	public constructor() {
		const x = Impulse(-1);

		this.x = Impulse(
			(scope) => max(0, x.getValue(scope) - this._shift.getValue(scope)),
			(next) => {
				x.setValue(next);

				this.brushing.setValue((brushing, scope) => {
					// already brushing - keep going
					if (brushing) {
						return true;
					}

					const { px: brushingStartPx } = this.brushStart.getValue(scope);

					// not brushing yet
					if (brushingStartPx == null) {
						return false;
					}

					// start brushing only when a threshold is reached, otherwise it might be too annoying for a user
					return abs(next - brushingStartPx) > IS_BRUSHING_THRESHOLD_PX;
				});
			},
		);
	}

	public toBrushArea(scope: Scope): TimelineBrushAreaState {
		return {
			start: this.brushStart.getValue(scope).time,
			end: this.x,
		};
	}

	public isBrushing(scope: Scope): boolean {
		return this.brushing.getValue(scope);
	}

	public isHovering(scope: Scope): boolean {
		return this.hovering.getValue(scope);
	}

	public shouldPreventClick(scope: Scope): boolean {
		return this.preventsClick.getValue(scope);
	}

	public enterTimeline(x: number, containerLeft: number): void {
		batch(() => {
			this.hovering.setValue(true);
			this._shift.setValue(containerLeft);
			this.x.setValue(x);
		});
	}

	public leaveTimeline(): void {
		this.hovering.setValue(false);
	}

	public startBrushing(time: Time, px: number): void {
		batch(() => {
			this.preventsClick.setValue(false);
			this.brushStart.setValue({ time, px });
		});
	}

	public endBrushing(): void {
		batch(() => {
			this.brushing.setValue(false);
			this.brushStart.setValue({ time: -1 });
		});
	}

	public cancelBrushing(): void {
		batch(() => {
			this.preventsClick.setValue(true);
			this.endBrushing();
		});
	}
}
